import { HttpClient } from '@angular/common/http';
import {
	AfterViewInit,
	Component,
	OnDestroy,
	OnInit,
	ViewChild,
} from '@angular/core';
import { AtomFormationThematiqueTableSummaryDocumentsComponent } from '@app/atoms/formation-thematique/atom-formation-thematique-table-summary-documents/atom-formation-thematique-table-summary-documents.component';
import { ActivatedRoute } from '@angular/router';
import {
	BasePageComponent,
	PageSkeletonType,
} from '@app/abstracts/base-page/base-page.component';
import {
	DocumentAdministrative,
	DocumentAdministrativeService,
} from '@app/models/document-administrative';
import {
	DocumentPedagogique,
	DocumentPedagogiqueService,
} from '@app/models/document-pedagogique';
import { Formation, FormationService } from '@app/models/formation';
import { FormationSessionService } from '@app/models/formation-session';
import {
	FormationIntervention,
	FormationInterventionService,
} from '@app/models/formation-intervention';
import { FormationThematique } from '@app/models/formation-thematique/formation-thematique';
import { FormationThematiqueService } from '@app/models/formation-thematique/formation-thematique.service';
import {
	AttestationPresenceThematiqueSummary,
	AttestationPresenceThematiqueSummaryService,
} from '@app/models/attestation-presence-thematique-summary';
import { ErrorService } from '@app/services/error.service';
import { User } from '@app/models/user/user';

import { PageInterface, PageService } from '@app/services/page.service';
import { S3Service } from '@app/services/s3.service';
import { TranslateService } from '@ngx-translate/core';
import { NzMessageService, NzModalService } from 'ng-zorro-antd';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

@Component({
	selector: 'app-molecule-formation-intervention-details-admin',
	templateUrl:
		'./molecule-formation-intervention-details-admin.component.html',
	styleUrls: [
		'./molecule-formation-intervention-details-admin.component.less',
	],
})
export class MoleculeFormationInterventionDetailsAdminComponent
	extends BasePageComponent
	implements OnInit, OnDestroy, AfterViewInit
{
	protected skeleton: PageSkeletonType = 'detail';
	private unsubscribe$ = new Subject<void>();

	@ViewChild(AtomFormationThematiqueTableSummaryDocumentsComponent)
	summaryTableComponent: AtomFormationThematiqueTableSummaryDocumentsComponent;

	formationIntervention: FormationIntervention;
	formationId: string;
	formationLabel: string;
	sessionId: string;
	sessionLabel: string;

	contractReadonly = false;
	orderMissionReadonly = false;

	documents: DocumentPedagogique[] = [];
	documentContract: DocumentAdministrative[] = [];
	documentOrderMission: DocumentAdministrative[] = [];
	respondants: User[] = [];
	thematiques: FormationThematique[] = [];

	docsToGenerateContract: string[] = [
		'CDDC-AO2-RA-Distanciel',
		'CDDC-AO2-Distanciel',
		'CDDC-AO2-RA-Presentiel',
		'CDDC-AO2-Presentiel',
		'CDDC-AO2-clase-virtuelle',
		'CDD-AO2-RA-Distanciel',
		'CDD-AO2-Distanciel',
		'CDD-AO2-RA-Presentiel',
		'CDD-AO2-Presentiel',
		'CDD-AO2-RA-clase-virtuelle',
		'CDD-AO2-clase-virtuelle',
	];
	docsToGenerateOrderMission: string[] = [
		'ordre-de-mission-presentiel',
		'ordre-de-mission-distanciel',
		'ordre-de-mission-bl',
		'Charte de Deontologie NOVE Concept - MAJ 2025',
		'Charte de Deontologie NOVE Concept - BL',
	];

	loading = false;
	contratTravailLoading = false;
	ordreDeMissionLoading = false;
	summaryLoading = false;

	constructor(
		protected pageService: PageService,
		protected translate: TranslateService,
		protected formationInterventionService: FormationInterventionService,
		protected formationSessionService: FormationSessionService,
		protected documentPedagogiqueService: DocumentPedagogiqueService,
		protected documentAdministrativeService: DocumentAdministrativeService,
		protected formationService: FormationService,
		protected formationThematiqueService: FormationThematiqueService,
		protected attestationPresenceThematiqueSummaryService: AttestationPresenceThematiqueSummaryService,
		private route: ActivatedRoute,
		private s3Service: S3Service,
		private errorService: ErrorService,
		private messageConfirmation: NzMessageService,
		private http: HttpClient,
		private modal: NzModalService
	) {
		super(pageService, translate);
	}

	async ngOnInit() {
		super.ngOnInit();
		this.loading = true;
		this.formationId = this.route.snapshot.params.id;
		this.sessionId = this.route.snapshot.params.sessionId;
		await Promise.all([
			this.getFormationInterventionInfos(),
			this.getRespondants(),
		]);
		this.loading = false;
	}

	ngAfterViewInit() {
		super.ngAfterViewInit();
	}

	ngOnDestroy() {
		super.ngOnDestroy();
		this.unsubscribe$.next();
		this.unsubscribe$.complete();
	}

	async getRespondants() {
		const formation = await this.formationService.getAsAdmin(
			this.formationId,
			{
				_populate: ['respondants'],
			}
		);
		this.respondants = formation.respondantsExists()
			? (formation.props.respondants as User[])
			: [];
	}

	async getFormationInterventionInfos() {
		this.formationIntervention =
			await this.formationInterventionService.getAsAdmin(
				this.route.snapshot.params.interventionId,
				{
					_populate: [
						'formateur',
						'thematiques',
						'formation.sponsor.avatar',
						'documents',
						'ordre_de_mission',
						'contrat_travail',
					],
				}
			);
		const formation = this.formationIntervention.props
			.formation as Formation;

		// Get session information
		const session = this.sessionId
			? await this.formationSessionService.getAsAdmin(this.sessionId, {
					_populate: ['interventions.thematiques'],
			  })
			: null;
		this.sessionLabel = session?.getLabel();

		this.documents = this.formationIntervention.props
			.documents as DocumentPedagogique[];

		this.documentContract = [
			this.formationIntervention.props
				.contrat_travail as DocumentAdministrative,
		];

		this.documentOrderMission = [
			this.formationIntervention.props
				.ordre_de_mission as DocumentAdministrative,
		];

		this.thematiques = this.formationIntervention.props
			.thematiques as FormationThematique[];

		this.formationId = formation.getId();
		this.formationLabel = formation.getLabel();
		this.contractReadonly = this.isDocSigned(this.documentContract);
		this.orderMissionReadonly = this.isDocSigned(this.documentOrderMission);

		this.refreshPageData();
	}

	getPageData(): PageInterface {
		return {
			title: this.translate.instant(
				'page_title-formation_detail_intervention',
				{
					formationIntervention: this.formationIntervention
						? this.formationIntervention.getLabel()
						: '',
				}
			),
			breadcrumbs: [
				{
					label: this.translate.instant('top_bar-formation_list'),
					link: ['/formations/list'],
				},
				{
					label: this.formationLabel ? this.formationLabel : '',
					link: ['/formations', this.formationId, 'details'],
				},
				...(this.sessionId
					? [
							{
								label: this.sessionLabel,
								link: [
									'/formations',
									this.formationId,
									'session',
									this.sessionId,
									'details',
								],
							},
					  ]
					: []),
				{
					label: this.formationIntervention
						? this.formationIntervention.getLabel()
						: null,
				},
			],
			hideTopBar: false,
			hideMenu: false,
		};
	}

	isGenerateDisable(
		docType: 'ordre_de_mission' | 'contrat_travail'
	): boolean {
		return false;
	}

	setGenerateLoading(
		docType: 'ordre_de_mission' | 'contrat_travail',
		isLoading: boolean
	): void {
		if (docType === 'ordre_de_mission') {
			this.ordreDeMissionLoading = isLoading;
		} else if (docType === 'contrat_travail') {
			this.contratTravailLoading = isLoading;
		}
	}

	isUploadDisable(docType: 'ordre_de_mission' | 'contrat_travail'): boolean {
		return !!this.formationIntervention.props[docType];
	}

	isSendDisable(documents: DocumentAdministrative[]): boolean {
		return this.isDocSigned(documents);
	}

	downloadPdf(url: string, filename: string) {
		this.http
			.get(url, { responseType: 'blob' })
			.pipe(takeUntil(this.unsubscribe$))
			.subscribe((response) => {
				const file = new Blob([response], { type: response.type });

				const fileURL = URL.createObjectURL(file);
				const fakeDownload = document.createElement('a');
				fakeDownload.href = fileURL;
				fakeDownload.target = '_blank';
				fakeDownload.download = filename;
				document.body.appendChild(fakeDownload);
				fakeDownload.click();
				URL.revokeObjectURL(fileURL);
				fakeDownload.remove();
			});
	}

	async handleDownloadDocPedagogique(event: {
		idDoc: string;
		filename: string;
	}) {
		const { url } =
			await this.formationInterventionService.getPresignedDocPedagogiqueAsAdmin(
				this.formationIntervention.getId(),
				event.idDoc
			);

		this.downloadPdf(url, event.filename);
	}

	async handleDownloadDocumentAdministrative(
		event: { idDoc: string; filename: string },
		docType: string
	) {
		const { url } =
			await this.formationInterventionService.getPresignedDocAdmininistrativeAsAdmin(
				this.formationIntervention.props._id,
				docType
			);
		this.downloadPdf(url, event.filename);
	}

	async handleSendGrouped() {
		try {
			await this.formationInterventionService.sendEmailPedagogique(
				this.formationIntervention.props._id
			);
			this.createMessageConfirmationSendGrouped();
		} catch (error) {
			this.createMessageErrorSend();
			this.errorService.handle(error);
		}
	}

	async handleGenerate(
		template: string,
		type: 'ordre_de_mission' | 'contrat_travail'
	): Promise<void> {
		this.setGenerateLoading(type, true);
		try {
			// Handle generation based on type

			// Existing logic for other document types
			await this.formationInterventionService.generate(
				template,
				type,
				this.formationIntervention.props._id
			);

			// Refresh data after generation
			await this.getFormationInterventionInfos();

			// Show success message
			this.messageConfirmation.success(
				this.translate.instant('common_generate') +
					' ' +
					this.translate.instant('common_success')
			);
		} catch (error) {
			this.errorService.handle(error);
		} finally {
			this.setGenerateLoading(type, false);
		}
	}

	async handleDocAdminUpload(
		file: File,
		previousDoc: DocumentAdministrative[],
		type: 'ordre_de_mission' | 'contrat_travail'
	): Promise<void> {
		const hasPreviousDoc = this.docExists(previousDoc);

		try {
			const res = await this.s3Service.uploadFile(file);
			if (res) {
				// create administrative document
				const docAdded =
					await this.documentAdministrativeService.createAsAdmin({
						label: file.name,
						status: hasPreviousDoc ? 'signed' : 'created',
						uri: res,
					});

				// update formation intervention with the document adminstrative id
				await this.formationInterventionService.updateAsAdmin(
					this.formationIntervention.getId(),
					{
						[type]: docAdded.getId(),
					}
				);

				// upload administrative document to the secure bucket
				await this.formationInterventionService.uploadDocumentAdministrative(
					this.formationIntervention.props._id,
					type,
					res
				);

				// update info
				await this.getFormationInterventionInfos();
			}
		} catch (error) {
			this.errorService.handle(error);
		}
	}

	async handleDocPedagogiqueUpload(file: File) {
		try {
			const res = await this.s3Service.uploadFile(file);
			if (res) {
				// create pedagogique document
				const docAdded =
					await this.documentPedagogiqueService.createAsAdmin({
						label: file.name,
						status: 'created',
						uri: res,
					});
				const allDocs = [
					...this.documents.map((doc) => doc.getId()),
					docAdded.getId(),
				];

				// update formation intervention with all the documents id
				await this.formationInterventionService.updateAsAdmin(
					this.formationIntervention.getId(),
					{
						documents: allDocs,
					}
				);

				// upload administrative document to the secure bucket
				await this.formationInterventionService.uploadDocumentPedagogique(
					this.formationIntervention.props._id,
					docAdded.getId(),
					res,
					file.name
				);

				// update info
				await this.getFormationInterventionInfos();
			}
		} catch (error) {
			this.errorService.handle(error);
		}
	}

	async handleReadDocumentAdministrative(docType: string) {
		const { url } =
			await this.formationInterventionService.getPresignedDocAdmininistrativeAsAdmin(
				this.formationIntervention.props._id,
				docType
			);
		window.open(url, '_blank');
	}

	async handleReadDocumentPedagogique(id: string) {
		const { url } =
			await this.formationInterventionService.getPresignedDocPedagogiqueAsAdmin(
				this.formationIntervention.props._id,
				id
			);
		window.open(url, '_blank');
	}

	async handleSendPedagogique(id: string) {
		try {
			if (!id) return '';
			await this.formationInterventionService.sendEmailPedagogique(
				this.formationIntervention.props._id,
				id
			);
			this.createMessageConfirmationSend();
		} catch (error) {
			this.createMessageErrorSend();
			this.errorService.handle(error);
		}
	}

	async handleSendAdministrative(
		id: string,
		docType: 'ordre_de_mission' | 'contrat_travail'
	) {
		try {
			if (!id) return '';
			await this.formationInterventionService.sendEmailAdministrative(
				this.formationIntervention.props._id,
				docType
			);
			this.createMessageConfirmationSend();
		} catch (error) {
			this.createMessageErrorSend();
			this.errorService.handle(error);
		}
	}

	async handleDeleteDocumentPedagogique(idDocument: string) {
		const documentsWithoutDeletedId = this.documents
			.filter((doc) => doc.getId() !== idDocument)
			.map((doc) => doc.getId());

		await this.formationInterventionService.updateAsAdmin(
			this.formationIntervention.getId(),
			{
				documents: documentsWithoutDeletedId,
			}
		);
		await this.getFormationInterventionInfos();
	}

	async handleDeleteDocumentAdministrative(
		docType: 'ordre_de_mission' | 'contrat_travail'
	) {
		const payload = { [docType]: null };
		await this.formationInterventionService.updateAsAdmin(
			this.formationIntervention.getId(),
			payload
		);
		await this.getFormationInterventionInfos();
	}

	private isDocSigned(doc: DocumentAdministrative[]) {
		if (this.docExists(doc)) {
			return doc[0].props.status === 'signed';
		}
		return false;
	}

	private docExists(doc: DocumentAdministrative[]) {
		return doc.length > 0 && doc[0] !== null;
	}

	createMessageConfirmationSend(): void {
		this.messageConfirmation.success(
			this.translate.instant('common_document_send')
		);
	}

	createMessageConfirmationSendGrouped(): void {
		this.messageConfirmation.success(
			this.translate.instant('common_document_send_grouped')
		);
	}

	createMessageErrorSend(): void {
		this.messageConfirmation.success(
			this.translate.instant('common_document_send_error')
		);
	}

	showDeleteConfirmOnDocAdmin(
		docType: 'ordre_de_mission' | 'contrat_travail'
	): void {
		this.modal.confirm({
			nzTitle: this.translate.instant('document_confirm_delete'),
			nzOkText: this.translate.instant('common_yes'),
			nzOnOk: async () => {
				await this.handleDeleteDocumentAdministrative(docType);
			},
			nzCancelText: this.translate.instant('common_no'),
		});
	}

	showDeleteConfirmOnDocPedagogique(idDocument: string): void {
		this.modal.confirm({
			nzTitle: this.translate.instant('document_confirm_delete'),
			nzOkText: this.translate.instant('common_yes'),
			nzOnOk: async () => {
				await this.handleDeleteDocumentPedagogique(idDocument);
			},
			nzCancelText: this.translate.instant('common_no'),
		});
	}

	// Attestation Presence Thematique Summary methods
	async handleGenerateSummary(thematiqueId: string): Promise<void> {
		try {
			this.summaryLoading = true;
			// Call generate with thematiqueId
			// This will create or update the summary record
			await this.formationThematiqueService.generatePresenceSummary(
				thematiqueId
			);
			// Refresh the summary table to show the newly generated document
			if (this.summaryTableComponent) {
				await this.summaryTableComponent.refresh();
			}
			this.messageConfirmation.success(
				this.translate.instant('common_generate') +
					' ' +
					this.translate.instant('common_success')
			);
		} catch (error) {
			this.errorService.handle(error);
		} finally {
			this.summaryLoading = false;
		}
	}

	async handleViewSummary(summaryId: string): Promise<void> {
		try {
			const { url } =
				await this.attestationPresenceThematiqueSummaryService.getPresignedUrl(
					summaryId
				);
			window.open(url, '_blank');
		} catch (error) {
			this.errorService.handle(error);
		}
	}

	async handleDeleteSummary(summaryId: string): Promise<void> {
		try {
			this.summaryLoading = true;
			await this.attestationPresenceThematiqueSummaryService.removeAsAdmin(
				summaryId
			);
			// Refresh the summary table to reflect the deletion
			if (this.summaryTableComponent) {
				await this.summaryTableComponent.refresh();
			}
			this.messageConfirmation.success(
				this.translate.instant('common_delete') +
					' ' +
					this.translate.instant('common_success')
			);
		} catch (error) {
			this.errorService.handle(error);
		} finally {
			this.summaryLoading = false;
		}
	}

	async handleDownloadSummary(event: {
		summaryId: string;
		filename: string;
	}): Promise<void> {
		try {
			const { url } =
				await this.attestationPresenceThematiqueSummaryService.getPresignedUrl(
					event.summaryId
				);
			this.downloadPdf(url, event.filename);
		} catch (error) {
			this.errorService.handle(error);
		}
	}

	showDeleteConfirmSummary(summaryId: string): void {
		this.modal.confirm({
			nzTitle: this.translate.instant('document_confirm_delete'),
			nzOkText: this.translate.instant('common_yes'),
			nzOnOk: async () => {
				await this.handleDeleteSummary(summaryId);
			},
			nzCancelText: this.translate.instant('common_no'),
		});
	}
}
